import React, { useEffect, useRef } from "react";

/**
 * Envuelve un contenedor con scroll horizontal (típicamente una tabla)
 * y añade una segunda barra de scroll horizontal ARRIBA que se mantiene
 * sincronizada con la barra inferior. Útil en tablas muy largas donde
 * el usuario no tiene por qué llegar hasta el fondo para desplazarse.
 *
 * Uso:
 *   <DualScrollX>
 *     <table>...</table>
 *   </DualScrollX>
 *
 * Detalles:
 *  - No modifica la estructura interna del contenido.
 *  - Sincroniza scrollLeft en ambas direcciones (evita loops con guard flag).
 *  - Reacciona a cambios de tamaño del contenido vía ResizeObserver, para que
 *    la barra fantasma superior refleje siempre el ancho real de la tabla.
 */
export function DualScrollX({ children, className = "", testid }) {
  const topRef = useRef(null);
  const bottomRef = useRef(null);
  const innerRef = useRef(null);
  const contentRef = useRef(null);
  const isSyncingTop = useRef(false);
  const isSyncingBottom = useRef(false);

  useEffect(() => {
    const contentEl = contentRef.current?.firstElementChild;
    const innerEl = innerRef.current;
    const topEl = topRef.current;
    const bottomEl = bottomRef.current;
    if (!contentEl || !innerEl || !topEl || !bottomEl) return;

    const syncWidth = () => {
      innerEl.style.width = `${contentEl.scrollWidth}px`;
    };
    syncWidth();

    const ro = new ResizeObserver(syncWidth);
    ro.observe(contentEl);

    const onTop = () => {
      if (isSyncingTop.current) { isSyncingTop.current = false; return; }
      isSyncingBottom.current = true;
      bottomEl.scrollLeft = topEl.scrollLeft;
    };
    const onBottom = () => {
      if (isSyncingBottom.current) { isSyncingBottom.current = false; return; }
      isSyncingTop.current = true;
      topEl.scrollLeft = bottomEl.scrollLeft;
    };
    topEl.addEventListener("scroll", onTop, { passive: true });
    bottomEl.addEventListener("scroll", onBottom, { passive: true });
    return () => {
      ro.disconnect();
      topEl.removeEventListener("scroll", onTop);
      bottomEl.removeEventListener("scroll", onBottom);
    };
  }, [children]);

  return (
    <div className={className} data-testid={testid}>
      <div
        ref={topRef}
        className="overflow-x-auto overflow-y-hidden"
        aria-hidden="true"
        style={{ height: 12 }}
      >
        <div ref={innerRef} style={{ height: 1 }} />
      </div>
      <div ref={bottomRef} className="overflow-x-auto" data-testid={testid ? `${testid}-content` : undefined}>
        <div ref={contentRef}>{children}</div>
      </div>
    </div>
  );
}

export default DualScrollX;
